import { Fab, Grow, Hidden } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import SaveIcon from '@material-ui/icons/Save'
import { makeStyles } from '@material-ui/core'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'
import React, { useContext, useEffect, useState } from 'react'
import { useRouteMatch, useHistory, matchPath } from 'react-router'
import type { Auth0User, ClientMetaDocument, OptionsDocument } from 'paintscout'
import {
  Button,
  ClientOptionsProvider,
  ConfirmationDialog,
  DialogStackContext,
  DialogStackPortal,
  PopupMenuItem,
  Spinner,
  Tab,
  Tabs
} from '@ui/stickybid'
import ClientAdministrationForm from '../../components/ClientAdministrationForm'
import ClientSettingsForm from '../../components/ClientSettingsForm'
import ErrorMessage from '../../components/ErrorMessage'

import { NavHeader } from '../../components/NavDrawer'
import NewUserDialog from '../../components/NewUserDialog'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { removeTypenames } from '../../util/removeTypenames'
import { Formik } from 'formik'
import RoutePrompt from '../../components/RoutePrompt'
import { calculateTaxRate, getIsTrial, sanitizeOptions } from '@paintscout/util/builder'
import { GET_CLIENT } from '../../graphql/queries/GET_CLIENT'
import { UPDATE_CLIENT } from '../../graphql/queries/UPDATE_CLIENT'
import type { GetClientResponse } from '../../graphql/queries/GET_CLIENT'
import { UPDATE_SALESMATE_COMPANY_TAGS } from 'src/graphql/queries/UPDATE_SALESMATE_COMPANY_TAGS'
import { getDeepObjectDiff } from '../../../../../packages/@paintscout/util/src/activity'

const useStyles = makeStyles({
  tabsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  mobileSaveButton: {
    position: 'fixed',
    bottom: 16,
    right: 16
  }
})

const validationSchema = Yup.object().shape({
  options: Yup.object().shape({
    options: Yup.object().shape({
      // companyName: Yup.object().shape({
      //   value: Yup.string().required('Required')
      // }),
      hourlyRate: Yup.object().shape({
        value: Yup.string().min(1).required()
      }),
      quotes: Yup.object().shape({
        customerViewExpiry: Yup.object().shape({
          enabled: Yup.boolean(),
          days: Yup.number().when('enabled', {
            is: true,
            then: Yup.number().required()
          }),
          months: Yup.number().when('days', {
            is: 0,
            then: Yup.number().moreThan(0).required()
          })
        }),
        customerInvoiceExpiry: Yup.object().shape({
          enabled: Yup.boolean(),
          days: Yup.number().when('enabled', {
            is: true,
            then: Yup.number().required()
          }),
          months: Yup.number().when('days', {
            is: 0,
            then: Yup.number().moreThan(0).required()
          })
        })
      })
    })
  }),
  internalOptions: Yup.object().shape({
    options: Yup.object().shape({
      integrations: Yup.object().shape({
        stripe: Yup.object().shape({
          feeRate: Yup.number().min(0),
          currency: Yup.string().oneOf(['USD', 'CAD', 'AUD'])
        })
      })
    })
  })
})

export default function EditClient() {
  const match = useRouteMatch<{ id: string; tab: string; section: string }>()
  const history = useHistory()
  const { id } = match.params
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const { enqueueSnackbar } = useSnackbar()
  const [tab, setTab] = useState(match.params.tab || 'administration')
  const [section, setSection] = useState(match.params.section)
  const classes = useStyles({})

  useEffect(() => {
    history.replace({
      pathname: `/clients/${id}/${tab}/${section || ''}`
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, section])

  const [updateClient, { loading: saving }] = useMutation(UPDATE_CLIENT)
  const [salesmate] = useMutation(UPDATE_SALESMATE_COMPANY_TAGS)

  const { data, loading, error } = useQuery<GetClientResponse>(GET_CLIENT, {
    variables: {
      id
    }
  })

  const options = data?.client?.options ? sanitizeOptions(data.client.options) : null
  const internalOptions = data?.client?.internalOptions
  const meta = data?.client?.meta
  const rates = data?.client?.rates
  const initialValues = { options, internalOptions, rates, meta }

  function handleNewUser() {
    openDialog(NewUserDialog, {
      client: {
        _id: meta._id,
        name: options.options.companyName.value as string
      },
      onConfirm: (user: Auth0User) => {
        history.push({
          pathname: `/users/${user.user_id}`
        })
        dismissDialog()
      }
    })
  }

  function changeTab(newTab: string) {
    setTab(newTab)
    setSection(undefined)
  }

  if (error) {
    return <ErrorMessage>{error.message}</ErrorMessage>
  }

  if (loading) {
    return <Spinner fullWidth fullHeight />
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, actions) => {
        const { options, internalOptions, rates, meta } = values
        try {
          const internalOptionsChanges = getDeepObjectDiff(
            initialValues.internalOptions.options,
            internalOptions.options
          )

          const handleSave = async () => {
            const updatedOptions = calculateTaxRate(options)

            const { trialActivated, trialInactivated } = await handleUpdateCrmForTrial({
              salesmate,
              meta: meta,
              oldMeta: initialValues.meta,
              options: updatedOptions
            })
            const updatedMeta = {
              ...meta,
              ...(meta.salesmateId && trialActivated && { expiredTrialCRM: false }),
              ...(meta.salesmateId && trialInactivated && { expiredTrialCRM: true })
            }

            const response = await updateClient({
              variables: {
                id,
                ...removeTypenames({ options: updatedOptions, internalOptions, rates, meta: updatedMeta })
              },
              refetchQueries: [{ query: GET_CLIENT, variables: { id } }]
            })
            const newValues = {
              options: response.data.updateClient.options ?? options,
              internalOptions: response.data.updateClient.internalOptions ?? internalOptions,
              rates: response.data.updateClient.rates ?? rates,
              meta: {
                ...meta,
                ...response?.data?.updateClient?.meta
              }
            }
            actions.resetForm({
              values: newValues
            })
            dismissDialog()
            enqueueSnackbar('Saved', { variant: 'success' })
          }

          if (internalOptionsChanges.length > 0) {
            openDialog(ConfirmationDialog, {
              title: `Warning - Updating Internal Options`,
              message: `You are about to update important Internal Options values, are you sure you wish to proceed?\n
              The values are ${Object.values(internalOptionsChanges).join(`, `)}`,
              scary: true,
              onConfirm: async () => {
                await handleSave()
              },
              onCancel: () => {
                dismissDialog()
                return
              }
            })
          } else {
            await handleSave()
          }
        } catch (e) {
          console.error(e)
          enqueueSnackbar('Unable to save client', { variant: 'error' })
        }
      }}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, values, dirty }) => {
        return (
          <ClientOptionsProvider options={values.options} rates={values.rates}>
            <DialogStackPortal />
            <RoutePrompt
              when={dirty}
              message={(next) => {
                if (
                  !matchPath(next.pathname, {
                    path: `/clients/${match.params.id}`
                  })
                ) {
                  return 'You have unsaved changes, are you sure?'
                }

                return true
              }}
            />
            <NavHeader
              title={options.options.companyName.value as string}
              showBackButton
              menuItems={
                <>
                  <PopupMenuItem icon={AddIcon} onClick={handleNewUser}>
                    New User
                  </PopupMenuItem>
                </>
              }
            />
            <div className={classes.tabsWrapper}>
              <Tabs value={tab} style={{ marginBottom: 20 }} onChange={(ev, value) => changeTab(value)}>
                <Tab value="administration" label="Administration" />
                <Tab value="settings" label="Settings" />
                <Tab value="admin-settings" label="Admin Settings" />
              </Tabs>
              <Hidden mdDown>
                <Grow in={dirty}>
                  <Button prominence={1} loading={isSubmitting} icon={SaveIcon} onClick={() => handleSubmit()}>
                    Save
                  </Button>
                </Grow>
              </Hidden>
            </div>
            {tab === 'administration' && <ClientAdministrationForm section={section} onSectionChange={setSection} />}
            {tab === 'settings' && <ClientSettingsForm section={section} onSectionChange={setSection} />}
            {tab === 'admin-settings' && (
              <ClientSettingsForm section={section} onSectionChange={setSection} isSuperAdmin />
            )}
            <Hidden lgUp>
              <div className={classes.mobileSaveButton}>
                <Grow in={dirty}>
                  <Fab disabled={!dirty || saving} color="primary" onClick={() => handleSubmit()}>
                    <SaveIcon />
                  </Fab>
                </Grow>
              </div>
            </Hidden>
          </ClientOptionsProvider>
        )
      }}
    </Formik>
  )
}

async function handleUpdateCrmForTrial(args: {
  salesmate: (args: any) => any
  meta: ClientMetaDocument
  oldMeta: ClientMetaDocument
  options: OptionsDocument
}): Promise<{ trialActivated: boolean; trialInactivated: boolean }> {
  const { salesmate, meta, oldMeta, options } = args
  const isTrial = getIsTrial({ meta, options })
  if (!isTrial || !meta.salesmateId) return { trialActivated: false, trialInactivated: false }

  const companyId = meta._id
  const oldExpiry = oldMeta.trialExpiry
  const newExpiry = meta.trialExpiry

  const now = Date.now()
  const trialActivated = oldExpiry < now && newExpiry > now
  const trialInactivated = oldExpiry > now && newExpiry < now

  if (!trialActivated && !trialInactivated) return { trialActivated, trialInactivated }

  const tagsToAdd = trialActivated ? ['Active Trial'] : trialInactivated ? ['Expired Trial'] : []
  const tagsToRemove = trialActivated ? ['Expired Trial'] : trialInactivated ? ['Active Trial'] : []

  try {
    await salesmate({
      variables: {
        eventName: 'updateCompanyTags',
        companyId,
        tagsToAdd,
        tagsToRemove
      }
    })
    return { trialActivated, trialInactivated }
  } catch (err) {
    console.log('error updating company tags for trial in admin')
    console.log(err)
    return { trialActivated: false, trialInactivated: false }
  }
}
